import { createAsyncThunk } from '@reduxjs/toolkit';
import { phonesUnsubscribed } from '../../shared/reducers/contacts';
import { fetchExtensionDetails } from '../../shared/store/thunks';
import { logActivity } from '../helpers/activityLog';
import appConfig from '../helpers/appConfig';
import type { ThunkAPI } from '../store/types';
import { fetchCallHistory } from './callHistory';

// This thunk is defined here to avoid dependency cycles between extension.ts and vcrm.ts
export const switchExtension = createAsyncThunk<void, string, ThunkAPI>(
  'extension/switchExtension',
  async (extensionId, thunkAPI) => {
    const { extension } = appConfig;
    const {
      vcrm: { isRegistered },
    } = thunkAPI.getState();
    if (!extension) {
      return;
    }

    const extensionModule = await import('./extension');
    thunkAPI.dispatch(extensionModule.extensionUnregistering());

    if (isRegistered) {
      const partnerCrm = await import('../helpers/partnerCrm');
      await partnerCrm.unload().catch((err) => {
        console.error(err);
      });
    }

    extension.isActive = false;

    extension.clearSubscriptions();
    thunkAPI.dispatch(phonesUnsubscribed());
    // UA has a timeout when stopping which disconnects transport 2 seconds
    // after stop is called, triggering the 'disconnected' event.
    // If we don't clear the listener, the extensionDisconnected redux
    // event will be fired leaving the UI in inconsistent state
    extension.removeAllListeners('disconnected');
    extension.stop();

    extension.once('unregistered', async () => {
      const extModule = await import('./extension');
      thunkAPI
        .dispatch(fetchExtensionDetails(extensionId))
        .then((fetchedExtension) => {
          const {
            // @ts-expect-error
            sip: { username, password, domain },
          } = fetchedExtension.payload;

          console.debug(`Registering new extension ${username}`);
          logActivity('default', 'REGISTER_EXTENSION', {
            reason: 'UNREGISTERED',
            username,
          });
          thunkAPI.dispatch(
            extModule.registerExtension({
              username,
              password,
              domain,
              audio: extModule.getStoredAudio(),
            }),
          );
        });
      const overrideParams = {
        endpointId: extensionId,
        page: 1,
      };
      thunkAPI.dispatch(fetchCallHistory({ overrideParams }));
    });
  },
);
